import React from 'react';
import { createPortal } from 'react-dom';

const SubscriptionInvoice = ({ isOpen, onClose, subscription }) => {
  const [logoLoaded, setLogoLoaded] = React.useState(false);
  const [logoError, setLogoError] = React.useState(false);

  // Preload the logo when component mounts
  React.useEffect(() => {
    if (isOpen) {
      const img = new Image();
      img.onload = () => setLogoLoaded(true);
      img.onerror = () => setLogoError(true);
      img.src = `${window.location.origin}/bm-logo.png`;
    }
  }, [isOpen]);

  // Add print styles
  React.useEffect(() => {
    if (isOpen) {
      const style = document.createElement('style');
      style.textContent = `
        @media print {
          body * {
            visibility: hidden;
          }
          .invoice-content, .invoice-content * {
            visibility: visible;
          }
          .invoice-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          /* Ensure images print properly */
          .invoice-logo {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
            opacity: 1 !important;
            visibility: visible !important;
          }
          
          /* Force image to be visible in print */
          .invoice-content img {
            opacity: 1 !important;
            visibility: visible !important;
            display: block !important;
          }
        }
      `;
      document.head.appendChild(style);

      return () => {
        document.head.removeChild(style);
      };
    }
  }, [isOpen]);

  if (!isOpen || !subscription) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';

    let date;
    
    // Handle ISO date strings (like "2026-02-04T17:20:17.000Z")
    if (typeof timestamp === 'string') {
      if (timestamp.includes('T') || timestamp.includes('-')) {
        // ISO date string
        date = new Date(timestamp);
      } else if (!isNaN(timestamp)) {
        // Numeric string (milliseconds)
        date = new Date(parseInt(timestamp));
      } else {
        date = new Date(timestamp);
      }
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';

    let date;
    
    // Handle ISO date strings (like "2026-02-04T17:20:17.000Z")
    if (typeof timestamp === 'string') {
      if (timestamp.includes('T') || timestamp.includes('-')) {
        // ISO date string
        date = new Date(timestamp);
      } else if (!isNaN(timestamp)) {
        // Numeric string (milliseconds)
        date = new Date(parseInt(timestamp));
      } else {
        date = new Date(timestamp);
      }
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return 0;

    const now = new Date();
    let expiry;

    // Handle ISO date strings (like "2026-04-05T17:20:17.000Z")
    if (typeof expiresAt === 'string') {
      if (expiresAt.includes('T') || expiresAt.includes('-')) {
        // ISO date string
        expiry = new Date(expiresAt);
      } else if (!isNaN(expiresAt)) {
        // Numeric string (milliseconds)
        expiry = new Date(parseInt(expiresAt));
      } else {
        expiry = new Date(expiresAt);
      }
    } else if (typeof expiresAt === 'number') {
      expiry = new Date(expiresAt);
    } else {
      expiry = new Date(expiresAt);
    }

    if (isNaN(expiry.getTime())) return 0;

    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const handlePrint = () => {
    // Ensure logo is loaded before printing
    if (!logoLoaded && !logoError) {
      // Wait for logo to load
      setTimeout(() => {
        handlePrint();
      }, 1000);
      return;
    }
    
    // Wait a moment to ensure everything is rendered
    setTimeout(() => {
      window.print();
    }, 300);
  };

  const handleDownload = () => {
    // Create a simple text invoice for download
    const invoiceText = `
BULKY MARKETING - SUBSCRIPTION INVOICE
=====================================

Invoice ID: #${subscription.id}
Date: ${formatDateTime(subscription.created_at)}

CUSTOMER INFORMATION:
- Subscription ID: ${subscription.id}
- Status: ${subscription.status.toUpperCase()}

PACKAGE DETAILS:
- Package: ${subscription.package?.name || 'N/A'}
- Price: ₹${subscription.package?.price || 'N/A'}
- Duration: ${subscription.package?.duration_days || 'N/A'} days
- Currency: ${subscription.package?.currency || 'INR'}

SUBSCRIPTION PERIOD:
- Start Date: ${formatDate(subscription.starts_at)}
- End Date: ${formatDate(subscription.expires_at)}
- Days Remaining: ${getDaysRemaining(subscription.expires_at)} days

PAYMENT INFORMATION:
- Payment ID: ${subscription.order?.razorpay_payment_id || 'N/A'}
- Amount Paid: ₹${subscription.order?.amount || 'N/A'}
- Payment Status: ${subscription.order?.status?.toUpperCase() || 'N/A'}

=====================================
BULKY MARKETING
Professional Marketing Solutions
Email: support@bulkymarketing.com
Website: www.bulkymarketing.com

Thank you for choosing Bulky Marketing!
    `;

    const blob = new Blob([invoiceText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulky-marketing-invoice-${subscription.id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return createPortal(
    <div className="modal-overlay">
      <div className="modal-container max-w-4xl">
        {/* Header */}
        <div className="modal-header no-print">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-white">
              <span className="material-icons-round text-sm">receipt</span>
            </div>
            <div>
              <h2 className="text-xl font-medium text-white tracking-tight">Financial Document</h2>
              <p className="text-[10px] text-white/90 font-normal uppercase tracking-wider leading-none mt-0.5">Invoice Specification System</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="w-8 h-8 hover:bg-white/20 text-white rounded-lg flex items-center justify-center transition-all"
              title="Generate Physical Copy"
            >
              <span className="material-icons-round text-sm">print</span>
            </button>
            <button
              onClick={handleDownload}
              className="w-8 h-8 hover:bg-white/20 text-white rounded-lg flex items-center justify-center transition-all"
              title="Capture Digital Extract"
            >
              <span className="material-icons-round text-sm">download</span>
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 hover:bg-white/20 text-white rounded-lg flex items-center justify-center transition-all"
            >
              <span className="material-icons-round text-sm">close</span>
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="modal-content !p-0 invoice-content">
          <div className="p-8">
            {/* Company Header */}
            <div className="flex flex-col md:flex-row justify-between items-start gap-6 h-auto mb-10">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-white border border-gray-100 rounded-xl flex items-center justify-center p-2 shadow-sm">
                  <img
                    src="/bm-logo.png"
                    alt="Bulky Marketing Logo"
                    className="max-w-full max-h-full object-contain invoice-logo"
                    onLoad={() => setLogoLoaded(true)}
                    onError={() => setLogoError(true)}
                    style={{ 
                      imageRendering: 'crisp-edges',
                      WebkitPrintColorAdjust: 'exact',
                      colorAdjust: 'exact'
                    }}
                  />
                </div>
                <div>
                  <h2 className="text-xl font-medium text-bulky-navy-800 tracking-tight">Bulky Marketing</h2>
                  <p className="text-[9px] font-normal text-gray-400 uppercase tracking-wider mt-0.5">Matrix Operative System</p>
                  <div className="mt-3 space-y-0.5 text-[9px] font-normal text-gray-400 uppercase tracking-wider">
                    <p>SECURE@BULKYMARKETING.COM</p>
                    <p>SYSTEM_ID: B-MAT-99X</p>
                  </div>
                </div>
              </div>

              <div className="md:text-right">
                <div className="bg-gray-50/50 border border-gray-100 rounded-xl p-5">
                  <h2 className="text-xs font-medium text-bulky-navy-400 uppercase tracking-widest mb-3">Invoice Identification</h2>
                  <div className="space-y-1.5">
                    <p className="text-[9px] font-normal text-bulky-navy-800 uppercase tracking-wider leading-none">
                      <span className="text-gray-400">UID:</span> <span className="font-normal">#{subscription.id.substring(0, 12)}</span>
                    </p>
                    <p className="text-[9px] font-normal text-bulky-navy-800 uppercase tracking-wider">
                      <span className="text-gray-400">TS:</span> {formatDateTime(subscription.created_at)}
                    </p>
                    <div className="pt-2">
                      <span className={`px-2 py-0.5 rounded text-[8px] font-normal uppercase tracking-wider ${subscription.status === 'active'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gray-800 text-white'
                        }`}>
                        STATUS: {subscription.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Package Details */}
            <div className="bg-bulky-navy-800 rounded-xl p-6 mb-6 text-white relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl group-hover:bg-white/10 transition-colors"></div>
              <h2 className="text-xs font-medium text-white/90 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                <span className="material-icons-round text-sm">inventory_2</span>
                Node Infrastructure Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h2 className="text-lg font-medium text-white tracking-tight mb-4">{subscription.package?.name || 'GENERIC NODE'}</h2>
                  <div className="space-y-2">
                    <div className="flex justify-between border-b border-white/10 pb-1.5">
                      <span className="text-[10px] font-normal text-white/85 uppercase tracking-wider">Base Valuation:</span>
                      <span className="text-[11px] font-medium text-white uppercase tracking-tight">₹{subscription.package?.price || '0.00'}</span>
                    </div>
                    <div className="flex justify-between border-b border-white/10 pb-1.5">
                      <span className="text-[10px] font-normal text-white/85 uppercase tracking-wider">Operational Span:</span>
                      <span className="text-[11px] font-medium text-white uppercase tracking-tight">{subscription.package?.duration_days || '0'} Days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[10px] font-normal text-white/85 uppercase tracking-wider">Pricing Matrix:</span>
                      <span className="text-[11px] font-medium text-white uppercase tracking-tight">{subscription.package?.currency || 'INR'}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                    <span className="material-icons-round text-white/80 text-sm mb-1.5 flex transition-colors group-hover:text-white">groups</span>
                    <p className="text-[9px] font-normal text-white/80 uppercase tracking-wider mb-0.5">Staff</p>
                    <p className="text-[11px] font-medium text-white uppercase tracking-tight">{subscription.package?.max_staff_members || 0} UNT</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                    <span className="material-icons-round text-white/80 text-sm mb-1.5 flex transition-colors group-hover:text-white">contacts</span>
                    <p className="text-[9px] font-normal text-white/80 uppercase tracking-wider mb-0.5">Contacts</p>
                    <p className="text-[11px] font-medium text-white uppercase tracking-tight">{subscription.package?.max_contacts?.toLocaleString() || 0} UNT</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                    <span className="material-icons-round text-white/80 text-sm mb-1.5 flex transition-colors group-hover:text-white">chat</span>
                    <p className="text-[9px] font-normal text-white/80 uppercase tracking-wider mb-0.5">Hubs</p>
                    <p className="text-[11px] font-medium text-white uppercase tracking-tight">{subscription.package?.max_whatsapp_instances || 0} WA</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                    <span className="material-icons-round text-white/80 text-sm mb-1.5 flex transition-colors group-hover:text-white">send</span>
                    <p className="text-[9px] font-normal text-white/80 uppercase tracking-wider mb-0.5">Flow</p>
                    <p className="text-[11px] font-medium text-white uppercase tracking-tight">{subscription.package?.max_bulk_messages_per_day || 0} DL</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Logical Spans */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              <div className="bg-emerald-50/30 border border-emerald-100 rounded-xl p-6 group">
                <h2 className="text-xs font-medium text-emerald-700 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                  <span className="material-icons-round text-sm">schedule</span>
                  Node Lifecycle Period
                </h2>
                <div className="space-y-4">
                  <div className="flex justify-between items-end border-b border-emerald-100 pb-2">
                    <span className="text-[10px] font-normal text-emerald-700 uppercase tracking-wider block">Deployment T-Zero</span>
                    <span className="text-xs font-medium text-emerald-900 uppercase tracking-tighter">{formatDate(subscription.starts_at)}</span>
                  </div>
                  <div className="flex justify-between items-end border-b border-emerald-100 pb-2">
                    <span className="text-[10px] font-normal text-emerald-700 uppercase tracking-wider block">Termination Timestamp</span>
                    <span className="text-xs font-medium text-emerald-900 uppercase tracking-tighter">{formatDate(subscription.expires_at)}</span>
                  </div>
                  {subscription.status === 'active' && (
                    <div className="bg-emerald-500 text-white rounded-lg p-3 text-center transition-all group-hover:scale-[1.02]">
                      <span className="text-[9px] font-normal uppercase tracking-[0.25em]">
                        {getDaysRemaining(subscription.expires_at)} Days Until Deprovisioning
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-bulky-blue-50/30 border border-bulky-blue-100 rounded-xl p-6 group">
                <h2 className="font-medium">
                  <span className="material-icons-round text-sm">payment</span>
                  Financial Transaction Log
                </h2>
                <div className="space-y-4">
                  <div className="flex justify-between items-end border-b border-bulky-blue-100 pb-2">
                    <span className="text-[10px] font-normal text-bulky-blue-700 uppercase tracking-wider block">Gateway fragment:</span>
                    <span className="text-[10px] font-medium text-bulky-blue-900 uppercase tracking-wider break-all ml-4 text-right">
                      {subscription.order?.razorpay_payment_id || 'LOCAL-EXEC'}
                    </span>
                  </div>
                  <div className="flex justify-between items-end border-b border-bulky-blue-100 pb-2">
                    <span className="text-[10px] font-normal text-bulky-blue-700 uppercase tracking-wider block">Settled Valuation:</span>
                    <span className="text-xs font-normal text-bulky-blue-800 uppercase tracking-tighter">₹{subscription.order?.amount || '0.00'}</span>
                  </div>
                  <div className="flex justify-between items-center bg-bulky-blue-100/50 rounded-lg p-2.5">
                    <span className="text-[8px] font-normal text-bulky-blue-600/80 uppercase tracking-wider">Confirmation State:</span>
                    <span className="px-2 py-0.5 bg-bulky-blue-600 text-white rounded text-[8px] font-normal uppercase tracking-wider">
                      {subscription.order?.status || 'VERIFIED'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 pt-8 text-center flex flex-col items-center">
              <div className="w-10 h-1 bg-bulky-navy-800 rounded-full mb-4"></div>
              <p className="text-[10px] font-normal text-bulky-navy-800 uppercase tracking-[0.2em] mb-1.5">Technical Matrix Authorized</p>
              <p className="text-[9px] font-normal text-gray-400 uppercase tracking-wider max-w-xs leading-relaxed">
                Persistent record of the resource allocation agreement between licensee and Bulky Marketing.
              </p>
              <div className="mt-6 pt-6 border-t border-gray-50 w-full text-center">
                <p className="text-[8px] font-normal text-gray-300 uppercase tracking-[0.3em]">Matrix Hash: {subscription.id.toUpperCase().substring(0, 16)}-AUTH</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SubscriptionInvoice;